import React, { useEffect, useState } from "react"
import { Formik } from "formik"
import { Link } from "react-router-dom"
import * as Sentry from "@sentry/react"
import Cookies from "js-cookie"
import { Navigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useMutation } from "@apollo/client"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import FormHelperText from "@mui/material/FormHelperText"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"

import { FielderTextField, HeaderLogo, PoweredByFielder, Seo } from "~/components"
import SnackbarMessage from "~/components/SnackbarMessage"
import { UPDATE_PASSWORD } from "~/queries/updatePassword"
import { VERIFY_PASSWORD_RESET_CODE } from "~/queries/verifyPasswordResetCode"
import { parseGraphQLErrorCode } from "~/util/parseGraphQLErrorCode"
import { getLandingRoute } from "~/util/authorization"
import { useAuth } from "~/context/AuthContext"
import { changeLanguage } from "~/i18n"
import { validateForm, isValid } from "~/util/passwordFormValidation"
import { Snack } from "~/types"

function VerifyPasswordResetCode() {
  const { t } = useTranslation()
  const { code } = useParams()
  const { login, logout } = useAuth()
  const [snack, setSnack] = useState<Snack>()
  const [username, setUsername] = useState<string>("")
  const [newAuthToken, setNewAuthToken] = useState<string>()
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [landingRoute, setLandingRoute] = useState<string>()
  const effectCalled = React.useRef(false)

  const [verifyPasswordResetCode, { loading: verifyLoading, error: verifyError }] = useMutation(
    VERIFY_PASSWORD_RESET_CODE,
    {
      onCompleted: (data) => {
        const { verifyPasswordResetCode } = data
        Cookies.set("authToken", verifyPasswordResetCode.authToken) // update the auth token in the Cookie - this is needed for the later call to updatePassword.
        setNewAuthToken(verifyPasswordResetCode.authToken)
        setUsername(verifyPasswordResetCode.username)
        changeLanguage(verifyPasswordResetCode.languageCode)
      },
      onError: (error) => {
        const errorCode = parseGraphQLErrorCode(error)
        let reportToSentry = true

        if (errorCode.includes("password.reset-code.invalid")) {
          reportToSentry = false
        } else if (errorCode.includes("password.reset-code.expired")) {
          reportToSentry = false
        }

        if (reportToSentry) {
          Sentry.captureException(error)
        }

        setSnack({ messageKey: errorCode, variant: "error" })
      },
    }
  )

  const [updatePassword, { loading, called }] = useMutation(UPDATE_PASSWORD, {
    onCompleted: (data) => {
      const { updatePassword } = data
      login?.(updatePassword.user, updatePassword.authToken)
      Sentry.setUser({ email: updatePassword.user.email })
      const lr = getLandingRoute(updatePassword.user)
      setLandingRoute(lr)
    },
    onError: (error) => {
      Sentry.captureException(error)
      const errorCode = parseGraphQLErrorCode(error)
      setSnack({ messageKey: errorCode, variant: "error" })
    },
  })

  useEffect(() => {
    if (effectCalled.current) {
      return
    }

    if (logout && code && verifyPasswordResetCode) {
      effectCalled.current = true
      logout()
      verifyPasswordResetCode({ variables: { code } })
    }
  }, [logout, code, verifyPasswordResetCode])

  if (landingRoute) {
    return <Navigate replace state={{ from: "/app/login" }} to={landingRoute} />
  }

  return (
    <>
      <Seo title={t("page.resetPassword.title")} />
      {snack ? <SnackbarMessage onClose={() => setSnack(undefined)} snack={snack} /> : null}
      <Box
        component="main"
        sx={{
          height: "100vh",
          backgroundColor: (theme) => theme.fielderScreens.login.backgroundColor,
        }}
      >
        <Formik
          initialValues={{
            password: "",
            confirmPassword: "",
          }}
          validate={validateForm}
        >
          {({ values, errors, touched, handleBlur, handleChange }) => (
            <form data-testid="resetPasswordForm">
              <Grid
                container
                sx={{
                  width: "100%",
                  maxWidth: "370px",
                  minWidth: "240px",
                  margin: "0 auto",
                  paddingTop: "10%",
                  background: (theme) => theme.fielderScreens.login.backgroundColor,
                }}
              >
                <Grid
                  data-testid="logo"
                  item
                  sx={{
                    margin: "auto 10px",
                  }}
                  xs={12}
                >
                  <HeaderLogo width="100%" />
                </Grid>
                <Grid
                  item
                  sx={(theme) => ({
                    margin: "auto 10px",
                    padding: "22px",
                    paddingTop: "10px",
                    backgroundColor: "#fff",
                    [theme.breakpoints.down("xs")]: {},
                    border: `1px solid ${theme.fielderScreens.login.backgroundColor}`,
                    borderRadius: "4px",
                  })}
                  xs={12}
                >
                  {verifyLoading ? (
                    <div
                      css={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        paddingBottom: "12.5rem",
                      }}
                    >
                      <h1
                        css={{
                          margin: 0,
                        }}
                        id="title"
                      >
                        {t("page.resetPassword.verifyingCode")}
                      </h1>
                      <CircularProgress />
                    </div>
                  ) : null}
                  {!verifyLoading && verifyError ? (
                    <div>
                      <h1
                        css={{
                          fontSize: "1.7em",
                          marginBottom: "0.625rem",
                        }}
                        id="title"
                      >
                        {t("page.resetPassword.verificationCodeInvalid.title")}{" "}
                      </h1>
                      <p id="invalidCodeMessageBody">
                        {t("page.resetPassword.verificationCodeInvalid.message")}
                      </p>
                      <div>
                        <Link
                          state={{ username }}
                          style={{
                            fontWeight: "bold",
                            marginRight: "0.625rem",
                            textDecoration: "underline",
                            color: "inherit",
                          }}
                          to="/app/forgot-password"
                        >
                          {t("page.resetPassword.verificationCodeInvalid.resendButtonLabel")}
                        </Link>
                      </div>
                    </div>
                  ) : null}
                  {!verifyLoading && !verifyError && (
                    <>
                      <h1
                        css={{
                          margin: 0,
                        }}
                        id="title"
                      >
                        {t("page.resetPassword.title")}
                      </h1>
                      <div
                        css={{
                          marginBottom: "0.625rem",
                          marginTop: "0.625rem",
                        }}
                        data-testid="messageBody"
                      >
                        {t("page.resetPassword.prompt")}
                        <ul
                          css={{
                            fontSize: "0.95em",
                            listStyle: "none",
                            paddingLeft: "0.75rem",
                          }}
                        >
                          {["length", "number", "lowercase", "uppercase", "special"].map(
                            (c: string) => (
                              <li
                                css={{
                                  color: !touched.password
                                    ? "#747474"
                                    : errors.password && !errors.password[c]
                                      ? "green"
                                      : "red",
                                }}
                                key={c}
                              >
                                <div>
                                  {errors.password && !errors.password[c] ? (
                                    <span
                                      css={{
                                        position: "absolute",
                                      }}
                                    >
                                      &#10003;
                                    </span>
                                  ) : null}
                                  {touched.password && errors.password && errors.password[c] ? (
                                    <span
                                      css={{
                                        position: "absolute",
                                      }}
                                    >
                                      &#10007;
                                    </span>
                                  ) : null}
                                  <span
                                    css={{
                                      marginLeft: "0.75rem",
                                    }}
                                  >
                                    {t(`page.resetPassword.criteria.${c}`)}
                                  </span>
                                </div>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                      <Box sx={classes.fieldContainer}>
                        <FielderTextField
                          InputProps={{
                            endAdornment: (
                              <InputAdornment data-testid="togglePasswordVisibility" position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={(e) => {
                                    e.preventDefault()
                                    setShowPassword((val) => !val)
                                  }}
                                  tabIndex={-1}
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          autoComplete="off"
                          autoFocus
                          data-testid="password"
                          fullWidth
                          id="password"
                          inputProps={{ maxLength: 255 }}
                          label={t("page.resetPassword.newPassword")}
                          margin="normal"
                          name="password"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          required
                          type={showPassword ? "text" : "password"}
                          value={values.password}
                        />
                      </Box>
                      <Box sx={classes.fieldContainer}>
                        <FielderTextField
                          autoComplete="off"
                          data-testid="confirmPassword"
                          fullWidth
                          id="confirmPassword"
                          inputProps={{ maxLength: 255 }}
                          label={t("page.resetPassword.confirmPassword")}
                          margin="normal"
                          name="confirmPassword"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          required
                          type={showPassword ? "text" : "password"}
                          value={values.confirmPassword}
                        />
                        {errors.confirmPassword && touched.confirmPassword ? (
                          <FormHelperText error id="confirmPasswordError">
                            {t(errors.confirmPassword)}
                          </FormHelperText>
                        ) : null}
                      </Box>
                      <Button
                        color="primary"
                        data-testid="submitButton"
                        disabled={
                          !(values.password && values.confirmPassword) ||
                          !isValid(errors) ||
                          loading
                        }
                        fullWidth
                        id="submitButton"
                        onClick={() => {
                          if (!newAuthToken) {
                            console.error("there is no new auth token!")
                            const errorCode = "page.resetPassword.error.noNewAuthToken"
                            Sentry.captureMessage(
                              `Error: The new auth token from verifyPasswordResetCode was not available when the user clicked the Update Password button. The user email is: ${username}`,
                              "error"
                            )
                            setSnack({ messageKey: errorCode, variant: "error" })
                            return
                          } else if (Cookies.get("authToken") !== newAuthToken) {
                            console.warn(
                              `Warning: The new auth token has not been stored when user ${username} attempted to reset their password! Setting the auth token now...`
                            )
                            Cookies.set("authToken", newAuthToken)
                            Sentry.captureMessage(
                              "Error: The new auth token from verifyPasswordResetCode was not available when the user clicked the Update Password button. The user email is: " +
                                username,
                              "warning"
                            )
                          }
                          // Add a little delay before submitting, just to try to reduce
                          // the chance of any potential race condition between when we set the token
                          // and when we make the network call. The updated token MUST be ready in browser storage
                          // before we make the network call, or it will not work and lead to lots of UX/customer-support problems.
                          setTimeout(() => {
                            updatePassword({
                              variables: { username, password: values.password },
                            })
                          }, 100)
                        }}
                        sx={{
                          marginTop: "1.25rem",
                          backgroundColor: (theme) => theme.palette.primary.main,
                          height: "50px",
                        }}
                        variant="contained"
                      >
                        {called && loading ? (
                          <CircularProgress size={20} thickness={6.0} />
                        ) : (
                          <span>{t("page.resetPassword.submitButton")}</span>
                        )}
                      </Button>
                    </>
                  )}
                  <div
                    css={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      marginTop: "2.5rem",
                      marginBottom: "-0.625rem",
                      color: "#747474",
                      "& a": {
                        color: "#747474",
                      },
                    }}
                  >
                    <a href="/terms" target="_blank">
                      {t("termsOfService", "Terms of Service")}
                    </a>
                    <a href="/privacy" target="_blank">
                      {t("privacyPolicy", "Privacy Policy")}
                    </a>
                  </div>
                </Grid>
              </Grid>
              <PoweredByFielder />
            </form>
          )}
        </Formik>
      </Box>
    </>
  )
}

const classes = {
  fieldContainer: {
    marginBottom: "1.25rem",
  },
} as const

export default VerifyPasswordResetCode
